import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Download,
  FilterList
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../../request/http-common';

const PaymentProofs = () => {
  const [selectedProof, setSelectedProof] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [reviewStatus, setReviewStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  const queryClient = useQueryClient();

  // Fetch payment proofs
  const { data: paymentProofs, isLoading, refetch } = useQuery(
    ['payment-proofs', filterStatus, filterType],
    async () => {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('transaction_type', filterType);
      
      const response = await apiClient.get(`/api/admin/payment-proofs?${params}`);
      return response.data;
    }
  );

  // Review payment proof mutation
  const { mutate: reviewPaymentProof, isLoading: isReviewing } = useMutation(
    async ({ proofId, status, notes }) => {
      return await apiClient.post(`/api/admin/payment-proof/${proofId}/review`, {
        status,
        admin_notes: notes
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['payment-proofs']);
        setReviewDialog(false);
        setSelectedProof(null);
        setReviewStatus('');
        setAdminNotes('');
      }
    }
  );

  const handleReview = () => {
    if (!selectedProof || !reviewStatus) return;
    
    reviewPaymentProof({
      proofId: selectedProof.id,
      status: reviewStatus,
      notes: adminNotes
    });
  };

  const openReviewDialog = (proof) => {
    setSelectedProof(proof);
    setReviewStatus(proof.status === 'pending' ? '' : proof.status);
    setAdminNotes(proof.admin_notes || '');
    setReviewDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  const formatFileSize = (bytes) => {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    
    return `${size.toFixed(2)} ${units[unitIndex]}`;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading payment proofs...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        Payment Proofs Management
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Filter by Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Filter by Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  label="Filter by Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="funding">Funding</MenuItem>
                  <MenuItem value="withdrawal">Withdrawal</MenuItem>
                  <MenuItem value="depay">Depay</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                onClick={() => {
                  setFilterStatus('');
                  setFilterType('');
                  refetch();
                }}
                fullWidth
                startIcon={<FilterList />}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payment Proofs Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Transaction Type</TableCell>
                  <TableCell>Transaction ID</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentProofs?.payment_proofs?.data?.map((proof) => (
                  <TableRow key={proof.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 1 }}>
                          {proof.user?.name?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {proof.user?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {proof.user?.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={proof.transaction_type} 
                        variant="outlined" 
                        size="small"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        #{proof.transaction_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {proof.original_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(proof.file_size)} • {proof.file_type}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={proof.status} 
                        color={getStatusColor(proof.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(proof.created_at).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(proof.created_at).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View File">
                          <IconButton
                            size="small"
                            href={proof.file_url}
                            target="_blank"
                            color="primary"
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Review">
                          <IconButton
                            size="small"
                            onClick={() => openReviewDialog(proof)}
                            color="secondary"
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {paymentProofs?.payment_proofs?.data?.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">
                No payment proofs found
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialog} onClose={() => setReviewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Review Payment Proof</DialogTitle>
        <DialogContent>
          {selectedProof && (
            <Box>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    User
                  </Typography>
                  <Typography variant="body2">
                    {selectedProof.user?.name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Transaction
                  </Typography>
                  <Typography variant="body2">
                    {selectedProof.transaction_type} #{selectedProof.transaction_id}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    File
                  </Typography>
                  <Typography variant="body2">
                    {selectedProof.original_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Status
                  </Typography>
                  <Chip 
                    label={selectedProof.status} 
                    color={getStatusColor(selectedProof.status)}
                    size="small"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Review Status</InputLabel>
                <Select
                  value={reviewStatus}
                  onChange={(e) => setReviewStatus(e.target.value)}
                  label="Review Status"
                  required
                >
                  <MenuItem value="approved">Approve</MenuItem>
                  <MenuItem value="rejected">Reject</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Admin Notes"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes about this review..."
              />

              {selectedProof.admin_notes && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Previous Notes:</Typography>
                  <Typography variant="body2">{selectedProof.admin_notes}</Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleReview} 
            variant="contained"
            disabled={!reviewStatus || isReviewing}
          >
            {isReviewing ? 'Updating...' : 'Update Review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentProofs;